import Cookies from "universal-cookie";
import GoogleAnalyticsHandler from "../../../utils/googleAnalyticsHandler";
import Router from "../Router/Router";
import DevTools from "./components/DevTools/index.tsx";
import CloudflareCaptcha from "../../Reusable/Recaptcha/components/CloudflareCaptcha/index.tsx";
import { AdditionalScripts } from "./AdditionalScripts";
import { useMsal } from "@azure/msal-react";
import { useUser } from "../../../contexts/userContext/userContext.js";
import { useEffect } from "react";
import { useContent } from "../../../contexts/contentContext/contentContext.js";
import { InteractionRequiredAuthError } from "@azure/msal-browser";
import { GoogleOAuthProvider } from "@react-oauth/google";
import { CustomDomainPagesE } from "../../../models/enums.ts";
import checkCurrDomainIsOrgDomain from "../../../helpers/checkCurrDomainIsOrgDomain.ts";
import type { UserDataT } from "../../../models/models.js";
import dayjs from "dayjs";

const cookies = new Cookies();

interface PageContentI {
  isBasicSettingsInitialized: boolean;
  isMsalInitialized?: boolean;
}

/**
 * Renders the main content of the page.
 *
 * @component
 * @param {PageContentI} props - The component props.
 * @returns {JSX.Element} The rendered component.
 */

const MAX_ALLOWED_HOURS = 72;

const PageContent = (props: PageContentI) => {
  const { isBasicSettingsInitialized, isMsalInitialized } = props;
  const { instance, accounts, inProgress } = useMsal();
  const { getUserData, logout } = useUser();
  const { getBasicSettingsData } = useContent();

  useEffect(() => {
    if (accounts?.[0] && !getUserData()?.UserId) {
      instance?.logout();
    }

    const isCustomDomainPage = Object.values(CustomDomainPagesE).some((cdp) =>
      window.location.pathname.includes(cdp)
    );
    const userBucketName = getUserData()?.BucketName;

    // logout in case user in some way enters custom domain pages and didn't join to organization
    if (
      getUserData()?.BucketName &&
      isCustomDomainPage &&
      userBucketName !== window.location.origin &&
      window.location.hostname !== "localhost" &&
      checkCurrDomainIsOrgDomain()
    ) {
      logout();
    }
  }, [getUserData()?.UserId]);

  // Check if the user is a sub-user and if the token is expired
  // logout the user.
  useEffect(() => {
    async function checkToken() {
      // If you are logged in with Microsoft SSO, get the token silently
      if (
        (getUserData() && instance && accounts?.[0] && isMsalInitialized,
        !inProgress,
        getBasicSettingsData()?.microsoftClientId)
      ) {
        try {
          const response = await instance.acquireTokenSilent({
            scopes: ["User.Read"],
            account: accounts[0],
          });
          console.log("Acquire token silent: ", response);
        } catch (error) {
          // If token is expired
          if (error instanceof InteractionRequiredAuthError) {
            if (
              error.errorCode.includes("blocked") ||
              error.errorMessage.includes("deleted")
            ) {
              logout();
              instance?.logoutPopup();

              return;
            }

            if ((getUserData() as UserDataT)?.IsSubUser) {
              console.log("Acquire token silent: ", error);

              logout();
              instance?.logoutPopup();

              // If the user is a sub-user, logout
              const loginDate = cookies.get("loginDate");
              const now = dayjs();
              const hoursSinceLogin = now.diff(dayjs(loginDate), "hour");
              if (hoursSinceLogin > MAX_ALLOWED_HOURS) {
                logout();
                instance?.logoutPopup();
              }
            }
          }
        }
      }
    }

    checkToken();
  }, [
    getUserData()?.UserId,
    getUserData()?.IsSubUser,
    instance,
    accounts,
    isMsalInitialized,
    getBasicSettingsData()?.microsoftClientId,
    inProgress,
  ]);

  return (
    <>
      <DevTools />
      {/* adding google scripts only to production environment */}
      <CloudflareCaptcha sitekey={getBasicSettingsData()?.turnstileKey} />
      <AdditionalScripts />
      <GoogleAnalyticsHandler />
      {/* In order to have fade-in effect, header and footer were moved to RouterHelper.js */}
      {isBasicSettingsInitialized && (
        <GoogleOAuthProvider clientId={getBasicSettingsData()?.googleClientId}>
          <Router />
        </GoogleOAuthProvider>
      )}
    </>
  );
};

export default PageContent;
